import { getManagedCredential } from "../auth-refresh-coordinator"
import { BACKEND_URI, fetchWithAuthRetry, getHeaders } from "./_shared"

/** The three personal sources. Each has its own Entra app, scope and stored consent. */
export const PERSONAL_SOURCES = ["onedrive", "mail", "chats"] as const
export type PersonalSource = (typeof PERSONAL_SOURCES)[number]

export type PersonalSourceConsentState = "none" | "active" | "withdrawn" | "purged"

/** Whether FILTH has been told about the user's last consent or withdrawal. */
export type PersonalSourceFilthAck = "pending" | "acked" | null

export type PersonalSourceConnection = {
  source: PersonalSource
  consent_state: PersonalSourceConsentState
  status?: string
  connected: boolean
  reconnect_required: boolean
  withdrawn_at?: string | null
  grace_ends_at?: string | null
  last_refreshed_at?: string | null
  filth_ack?: PersonalSourceFilthAck
}

export type PersonalSourceView = PersonalSourceConnection & {
  available: boolean
  /** Server-side verdict: the admin enabled this source for one of the user's groups. */
  eligible: boolean
  enabled: boolean
  history_window_days: number
}

export type PersonalSourcesResponse = {
  available: boolean
  sources: PersonalSourceView[]
  grace_days: number
}

export type PersonalSourceWithdrawal = PersonalSourceConnection & {
  entra_revocation_required?: boolean
  note?: string
  grace_days?: number
}

const url = `${BACKEND_URI}/personal-sources`

export async function getPersonalSources(): Promise<PersonalSourcesResponse> {
  return (await fetchWithAuthRetry(url, { headers: getHeaders(getManagedCredential()) })).json()
}

export async function authorizePersonalSource(
  source: PersonalSource,
  redirectUri: string
): Promise<{ auth_url: string; state: string; source: PersonalSource }> {
  return (
    await fetchWithAuthRetry(`${url}/${source}/authorize`, {
      method: "POST",
      headers: getHeaders(getManagedCredential()),
      body: JSON.stringify({ redirect_uri: redirectUri }),
    })
  ).json()
}

export async function completePersonalSource(
  source: PersonalSource,
  query: string
): Promise<PersonalSourceConnection> {
  return (
    await fetchWithAuthRetry(`${url}/${source}/callback?${query}`, {
      headers: getHeaders(getManagedCredential()),
    })
  ).json()
}

export async function withdrawPersonalSource(
  source: PersonalSource
): Promise<PersonalSourceWithdrawal> {
  return (
    await fetchWithAuthRetry(`${url}/${source}`, {
      method: "DELETE",
      headers: getHeaders(getManagedCredential()),
    })
  ).json()
}

export type SessionPersonalSearch = {
  session_id: string
  enabled: boolean
  updatedAt?: string | null
}

/**
 * Turn personal search on or off for one chat (owner only). The choice is stored on the
 * session, so every follow-up turn honours it. Turning it on in a shared chat answers 409.
 */
export async function setSessionPersonalSearch(
  sessionId: string,
  enabled: boolean
): Promise<SessionPersonalSearch> {
  return (
    await fetchWithAuthRetry(
      `${BACKEND_URI}/sessions/${encodeURIComponent(sessionId)}/personal-search`,
      {
        method: "PUT",
        headers: getHeaders(getManagedCredential()),
        body: JSON.stringify({ enabled }),
      }
    )
  ).json()
}
